import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Medal, Crown, ChevronRight, Flame, Star, Clock, CheckCircle,  Copy } from 'lucide-react';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { getInitials } from '../utils/avatarUtils';

const API_URL = '';

/**
 * March Madness Leaderboard Component
 * Displays the top 10 reviewers for the current week's challenge.
 * Only renders when challenge is active.
 */
const MarchMadnessLeaderboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [challengeData, setChallengeData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/challenges/leaderboard`);
      
      if (response.data.active) {
        setChallengeData(response.data);
      } else {
        setChallengeData(null);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching challenge leaderboard:', err);
      setError('Failed to load leaderboard');
      setLoading(false);
    }
  };

  // Don't render anything if challenge is not active
  if (loading) {
    return null; // Silent loading - don't show skeleton if challenge might not be active
  }

  if (!challengeData || error) {
    return null; // No active challenge - hide completely
  }

  const { challenge_name, week_start, week_end, leaderboard } = challengeData;

  // Format dates for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/reviewers`;
    const shareText = `🏀 Check out the March Madness Challenge on Mic Check Millionaire! Reviewers are competing for weekly prizes. See who's on top! 🏆`;
    
    // Check if mobile device (use native share only on mobile)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Try native share API only on mobile
    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: 'March Madness Challenge - Mic Check Millionaire',
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fall through to clipboard
        if (err.name === 'AbortError') return;
      }
    }
    
    // Desktop: Always copy to clipboard
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
      toast.success('Link copied to clipboard!', {
        description: 'Share it with your friends',
        duration: 3000,
      });
    } catch (err) {
      // Final fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = `${shareText}\n\n${shareUrl}`;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link copied to clipboard!');
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-5 h-5 text-yellow-500" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-400" />;
      case 3:
        return <Medal className="w-5 h-5 text-amber-600" />;
      default:
        return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-gray-500">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-yellow-500/30';
      case 2:
        return 'bg-gradient-to-r from-gray-300/20 to-gray-400/20 border-gray-400/30';
      case 3:
        return 'bg-gradient-to-r from-amber-600/20 to-orange-500/20 border-amber-600/30';
      default:
        return 'bg-white/50 border-gray-200';
    }
  };

  return (
    <section className="bg-gradient-to-br from-black via-gray-900 to-black py-8 mb-0 border-b-4 border-[#F2D857]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div className="flex items-center gap-3 mb-4 md:mb-0">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F2D857] to-amber-500 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-black" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
                {challenge_name || 'March Madness'} 
                <span className="text-[#F2D857]">Challenge</span>
              </h2>
              <p className="text-gray-400 text-sm flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Week of {formatDate(week_start)} - {formatDate(week_end)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={handleShare}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 hover:text-[#F2D857] transition-all gap-2"
              data-testid="share-challenge-btn"
            >
              <Copy className="w-4 h-4" />
              <span className="hidden sm:inline">Copy Link</span>
            </Button>
            <Button
              onClick={() => navigate('/leaderboard')}
              variant="outline"
              className="border-[#F2D857] text-[#F2D857] hover:bg-[#F2D857] hover:text-black transition-all"
              data-testid="view-full-leaderboard-btn"
            >
              View Full Leaderboard
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Prize Info */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-3 py-1">
            🥇 1st: $50
          </Badge>
          <Badge className="bg-gray-400/20 text-gray-300 border border-gray-400/30 px-3 py-1">
            🥈 2nd: $25
          </Badge>
          <Badge className="bg-amber-600/20 text-amber-400 border border-amber-600/30 px-3 py-1">
            🥉 3rd: $10
          </Badge>
          <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30 px-3 py-1">
            🎲 Raffle: $25
          </Badge>
        </div>

        {/* Leaderboard Table */}
        {leaderboard.length === 0 ? (
          <div className="bg-white/5 rounded-xl p-8 text-center border border-white/10">
            <Flame className="w-12 h-12 text-[#F2D857] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Be the First to Compete!</h3>
            <p className="text-gray-400">
              Start reviewing tracks to climb the leaderboard and win prizes.
            </p>
          </div>
        ) : (
          <div className="bg-white/5 rounded-xl overflow-hidden border border-white/10">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-white/5 border-b border-white/10">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Rank</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Reviewer</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">Live Days</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">Active Mins</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">Reviews</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">Points</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {leaderboard.slice(0, 10).map((entry) => (
                    <tr 
                      key={entry.reviewer_id} 
                      className={`hover:bg-white/5 transition-colors ${entry.rank <= 3 ? getRankBg(entry.rank) : ''}`}
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center w-8 h-8">
                          {getRankIcon(entry.rank)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10 border-2 border-white/20">
                            <AvatarImage src={entry.avatar_url} alt={entry.name} />
                            <AvatarFallback className="bg-[#F2D857] text-black font-bold">
                              {getInitials(entry.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-white">{entry.name}</p>
                            <p className="text-sm text-gray-400">@{entry.username}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-white font-medium">{entry.live_days}</span>
                        <span className="text-gray-500 text-xs ml-1">days</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-white font-medium">{Math.round(entry.active_minutes)}</span>
                        <span className="text-gray-500 text-xs ml-1">min</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-white font-medium">{entry.reviews}</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-[#F2D857] font-bold text-lg">{entry.points}</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {entry.is_eligible ? (
                          <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Eligible
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-500/20 text-gray-400 border border-gray-500/30">
                            In Progress
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-white/10">
              {leaderboard.slice(0, 5).map((entry) => (
                <div 
                  key={entry.reviewer_id} 
                  className={`p-4 ${entry.rank <= 3 ? getRankBg(entry.rank) : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8">
                      {getRankIcon(entry.rank)}
                    </div>
                    <Avatar className="w-10 h-10 border-2 border-white/20">
                      <AvatarImage src={entry.avatar_url} alt={entry.name} />
                      <AvatarFallback className="bg-[#F2D857] text-black font-bold">
                        {getInitials(entry.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-semibold text-white">{entry.name}</p>
                      <p className="text-xs text-gray-400">@{entry.username}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#F2D857] font-bold text-lg">{entry.points}</p>
                      <p className="text-xs text-gray-400">points</p>
                    </div>
                  </div>
                  <div className="flex justify-between mt-3 text-sm">
                    <span className="text-gray-400">{entry.live_days} days</span>
                    <span className="text-gray-400">{Math.round(entry.active_minutes)} min</span>
                    <span className="text-gray-400">{entry.reviews} reviews</span>
                    {entry.is_eligible && (
                      <Badge className="bg-green-500/20 text-green-400 border border-green-500/30 text-xs">
                        Eligible
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How to Qualify Info */}
        <TooltipProvider delayDuration={200}>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-400">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 cursor-help hover:text-gray-300 transition-colors">
                  <Star className="w-4 h-4 text-[#F2D857]" />
                  3+ Live Days per week
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs bg-gray-900 border-gray-700 text-white p-3">
                <p className="font-semibold text-[#F2D857] mb-1">Live Days</p>
                <p className="text-sm text-gray-300">
                  A "Live Day" counts when you hit both 30+ active minutes AND 3+ completed reviews in a single day. 
                  You need at least 3 qualifying days each week to be eligible for prizes.
                </p>
              </TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 cursor-help hover:text-gray-300 transition-colors">
                  <Clock className="w-4 h-4 text-[#F2D857]" />
                  30+ Active min/day
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs bg-gray-900 border-gray-700 text-white p-3">
                <p className="font-semibold text-[#F2D857] mb-1">Active Minutes</p>
                <p className="text-sm text-gray-300 mb-2">
                  We track real engagement — playing tracks, reviewing submissions, and interacting with your queue. 
                  Idle time (3+ min without activity) doesn't count.
                </p>
                <p className="text-xs text-gray-500 italic">
                  Fair play matters. Only genuine reviewing activity is tracked to keep the competition honest.
                </p>
              </TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 cursor-help hover:text-gray-300 transition-colors">
                  <CheckCircle className="w-4 h-4 text-[#F2D857]" />
                  3+ Reviews/day
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs bg-gray-900 border-gray-700 text-white p-3">
                <p className="font-semibold text-[#F2D857] mb-1">Completed Reviews</p>
                <p className="text-sm text-gray-300 mb-2">
                  A review counts when you move a submission to Spotlight or mark it as Finished. 
                  Quality feedback helps artists grow and earns you points!
                </p>
                <p className="text-xs text-gray-500 italic">
                  Each review = 5 points. Consistency is key to climbing the leaderboard.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>
    </section>
  );
};

export default MarchMadnessLeaderboard;
